import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getWarehouseListing, getWarehouseStates, getWarehouseCities, updateWarehouseStatus as updateWarehouseStatusApi } from 'services/warehouseService';
import { getPermissions } from 'services/authService';
import { getAuthPermissions, getAuthRoleId } from 'utils/auth';
import { formatDateTime } from 'utils/dateTime';
import { formatFirstLetterCapitalized } from 'utils/formatLabel';

export function isWarehouseActive(status) {
  if (status == null) return true;
  return status === true || Number(status) === 1;
}

function mapWarehouseRows(payload) {
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data?.data)
      ? payload.data.data
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.result)
          ? payload.result
          : Array.isArray(payload?.rows)
            ? payload.rows
            : [];

  return list.map((item) => ({
    id: item.id ?? '-',
    state_id: item.state_id ?? '',
    city_id: item.city_id ?? '',
    warehouse_name: formatFirstLetterCapitalized(item.warehouse_name),
    slug: item.slug || '-',
    state: formatFirstLetterCapitalized(item.State?.name),
    city: formatFirstLetterCapitalized(item.City?.name),
    address: formatFirstLetterCapitalized(item.address),
    capacity_unit: formatFirstLetterCapitalized(item.capacity_unit),
    country_code: item.country_code || '-',
    created_at: formatDateTime(item.created_at),
    created_by: item.created_by ?? '-',
    CreatedBy: formatFirstLetterCapitalized([item.CreatedBy?.first_name, item.CreatedBy?.last_name].filter(Boolean).join(' ')),
    lat: item.lat || '-',
    lng: item.lng || '-',
    manager_phone_number: item.manager_phone_number || '-',
    no_of_chambers: item.no_of_chambers ?? '-',
    no_of_docks: item.no_of_docks ?? '-',
    no_of_gates: item.no_of_gates ?? '-',
    postal_code: item.postal_code || item.potal_code || '-',
    status: isWarehouseActive(item.status),
    total_capacity: item.total_capacity ?? '-',
    updated_at: formatDateTime(item.updated_at),
    warehouse_manager_email_id: item.warehouse_manager_email_id || '-',
    warehouse_manager_name: formatFirstLetterCapitalized(item.warehouse_manager_name),
    warehouses_location: formatFirstLetterCapitalized(item.warehouses_location),
    warehouse_images: item.warehouse_images ?? item.images ?? []
  }));
}

function extractPagination(responseData, fallback = {}) {
  const pagination = responseData?.pagination || responseData?.data?.pagination || {};
  return {
    page: Number(pagination.page ?? fallback.page ?? 1),
    limit: Number(pagination.limit ?? fallback.limit ?? 10),
    total: Number(pagination.total ?? fallback.total ?? 0)
  };
}

export const updateWarehouseStatus = createAsyncThunk(
  'warehouse/updateStatus',
  async ({ warehouseId, status }, { rejectWithValue }) => {
    const response = await updateWarehouseStatusApi(warehouseId, status);
    if (!response.ok) {
      return rejectWithValue(response.data?.message || 'Failed to update warehouse status');
    }
    return { warehouseId, status: Boolean(status) };
  }
);

export const fetchWarehouseListing = createAsyncThunk('warehouse/fetchListing', async (params = {}, { rejectWithValue, signal }) => {
  const response = await getWarehouseListing({ ...params, signal });
  if (!response.ok) {
    return rejectWithValue(response.data?.message || 'Failed to fetch warehouse list');
  }
  return {
    rows: mapWarehouseRows(response.data),
    pagination: extractPagination(response.data, params)
  };
});

function extractStatesList(responseData) {
  const list = Array.isArray(responseData?.data) ? responseData.data : Array.isArray(responseData) ? responseData : [];
  return list.map((item) => ({ id: item.id, name: item.name || '-' }));
}

function extractCitiesList(responseData) {
  const list = Array.isArray(responseData?.data) ? responseData.data : Array.isArray(responseData) ? responseData : [];
  return list.map((item) => ({ id: item.id, name: item.name || '-', state_id: item.state_id }));
}

export const fetchWarehouseStates = createAsyncThunk('warehouse/fetchStates', async (_, { rejectWithValue }) => {
  const response = await getWarehouseStates();
  if (!response.ok) return rejectWithValue(response.data?.message || 'Failed to fetch states');
  return extractStatesList(response.data);
});

export const fetchWarehouseCities = createAsyncThunk(
  'warehouse/fetchCities',
  async (stateId, { rejectWithValue }) => {
    const response = await getWarehouseCities(stateId);
    if (!response.ok) return rejectWithValue(response.data?.message || 'Failed to fetch cities');
    return { stateId, list: extractCitiesList(response.data) };
  }
);

function extractPermissionTree(responseData) {
  const list = Array.isArray(responseData?.data)
    ? responseData.data
    : Array.isArray(responseData?.data?.data)
      ? responseData.data.data
      : Array.isArray(responseData)
        ? responseData
        : [];

  return list
    .filter((item) => Number(item?.status) === 1 || item?.status === true || item?.status == null)
    .map((item) => ({
      id: item?.id,
      name: item?.name || '',
      slug: item?.slug || '',
      sort: Number(item?.sort || 0),
      subPermissions: Array.isArray(item?.SubPermissions || item?.sub_permissions || item?.subPermissions)
        ? (item?.SubPermissions || item?.sub_permissions || item?.subPermissions)
            .filter((sub) => sub?.status === true || Number(sub?.status) === 1 || sub?.status == null)
            .map((sub) => ({
              id: sub?.id,
              name: sub?.name || '',
              slug: sub?.slug || '',
              sort: Number(sub?.sort || 0)
            }))
            .sort((a, b) => a.sort - b.sort)
        : []
    }))
    .sort((a, b) => a.sort - b.sort);
}

export const fetchManagePermissions = createAsyncThunk('warehouse/fetchManagePermissions', async (_, { rejectWithValue }) => {
  const roleId = getAuthRoleId();
  if (roleId === 1 || roleId === 2) {
    const response = await getPermissions();
    if (!response.ok) return rejectWithValue(response.data?.message || 'Failed to fetch permissions');
    return extractPermissionTree(response.data);
  }
  return extractPermissionTree(getAuthPermissions());
});

const warehouseSlice = createSlice({
  name: 'warehouse',
  initialState: {
    rows: [],
    status: 'idle',
    error: null,
    states: [],
    cities: [],
    citiesStateId: null,
    permissions: [],
    permissionsStatus: 'idle',
    permissionsError: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0
    },
    listingRequestId: null
  },
  reducers: {
    clearCities(state) {
      state.cities = [];
      state.citiesStateId = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWarehouseListing.pending, (state, action) => {
        state.status = 'loading';
        state.error = null;
        state.listingRequestId = action.meta.requestId;
      })
      .addCase(fetchWarehouseListing.fulfilled, (state, action) => {
        if (state.listingRequestId && state.listingRequestId !== action.meta.requestId) return;
        state.status = 'succeeded';
        state.rows = action.payload.rows;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchWarehouseListing.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch warehouse list';
      })
      .addCase(updateWarehouseStatus.fulfilled, (state, action) => {
        const { warehouseId, status } = action.payload;
        const row = state.rows.find((item) => String(item.id) === String(warehouseId));
        if (row) row.status = status;
      })
      .addCase(fetchWarehouseStates.fulfilled, (state, action) => {
        state.states = action.payload;
      })
      .addCase(fetchWarehouseCities.fulfilled, (state, action) => {
        state.cities = action.payload.list;
        state.citiesStateId = action.payload.stateId;
      })
      .addCase(fetchManagePermissions.pending, (state) => {
        state.permissionsStatus = 'loading';
        state.permissionsError = null;
      })
      .addCase(fetchManagePermissions.fulfilled, (state, action) => {
        state.permissionsStatus = 'succeeded';
        state.permissions = action.payload;
      })
      .addCase(fetchManagePermissions.rejected, (state, action) => {
        state.permissionsStatus = 'failed';
        state.permissionsError = action.payload || 'Failed to fetch permissions';
      });
  }
});

export const { clearCities } = warehouseSlice.actions;

export default warehouseSlice.reducer;
